/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import { call, debounce, put, select, takeLatest } from 'redux-saga/effects';
import { UPDATED_SETTINGS } from '@nebula/airglow-plugin-unified-shell/dist/actions';
import { SAVE_VALIDATION_PLUGIN_CONFIG } from '../actions';
import {
  STATEFUL_PLUGIN_SAVED,
  writeSessionSettingsToSession,
  writeValidationluginSettingsToOrg,
  writeValidationluginSettingsToUser
} from '../../common/actions';
import {
  selectAllValidationPlugins,
  selectOrgValidationPluginSettings,
  selectSessionRouteParam,
  selectSessionSettings,
  selectUserValidationPluginSettings
} from '../../common/selectors';
import {
  orgValidationUpdates,
  sessionValidationUpdates,
  userValidationUpdates
} from '../data/prefabs';
import { applyShellSettings } from '../../session-view/sagas/apply.shell.settings.saga';

export function* handleSaveValidationPluginConfig() {
  const sessionId = yield select(selectSessionRouteParam);
  const sessionSettings = yield select(selectSessionSettings);
  const sessionValue = yield select(sessionValidationUpdates.value);
  yield put(writeSessionSettingsToSession(
    R.assocPath([sessionId, 'validationPlugins'], sessionValue, sessionSettings)
  ));

  const userValue = yield select(userValidationUpdates.value);
  yield put(writeValidationluginSettingsToUser(userValue));

  const orgValue = yield select(orgValidationUpdates.value);
  yield put(writeValidationluginSettingsToOrg(orgValue));
}

export default function* watchUpdatePluginConfig() {
  yield takeLatest(SAVE_VALIDATION_PLUGIN_CONFIG, handleSaveValidationPluginConfig);
}

function* addNewPluginSettings(newPlugin, sessionSettings, userSettings, orgSettings) {
  const addSettings = (settings, namespace) => (!settings.namespace
    ? R.assoc(namespace, { level: 'error' }, settings) : settings);

  const sessionId = yield select(selectSessionRouteParam);
  const sessionValidationSettings = R.path([sessionId, 'validationPlugins'], sessionSettings);

  if (sessionValidationSettings) {
    const newSessionSettings = addSettings(sessionValidationSettings, newPlugin.namespace);
    yield put(writeSessionSettingsToSession(
      R.assocPath([sessionId, 'validationPlugins'], newSessionSettings, sessionSettings)
    ));
  }

  if (userSettings) {
    const newUserSettings = addSettings(userSettings, newPlugin.namespace);
    yield put(writeValidationluginSettingsToUser(newUserSettings));
  }

  if (orgSettings) {
    const newOrgSettings = addSettings(orgSettings, newPlugin.namespace);
    yield put(writeValidationluginSettingsToOrg(newOrgSettings));
  }
}

function* removeDeletedPluginSettings(sessionSettings, userSettings, orgSettings) {
  let settingsLens;

  const validationPlugins = yield select(selectAllValidationPlugins);
  const namespaces = R.pluck('namespace', validationPlugins);

  const sessionId = yield select(selectSessionRouteParam);
  const sessionValidationSettings = R.path([sessionId, 'validationPlugins'], sessionSettings);

  if (sessionValidationSettings) {
    settingsLens = R.lensPath([sessionId, 'validationPlugins']);
    yield put(writeSessionSettingsToSession(
      R.over(settingsLens, R.pick(namespaces), sessionSettings)
    ));
  }

  settingsLens = R.lensPath(['validationPlugins']);

  if (userSettings) {
    yield put(writeValidationluginSettingsToUser(R.pick(namespaces, userSettings)));
  }

  if (orgSettings) {
    yield put(writeValidationluginSettingsToOrg(R.pick(namespaces, orgSettings)));
  }
}

export function* handleStatefulPluginSaved({ payload }) {
  const orgValidationSettings = yield select(selectOrgValidationPluginSettings);
  const userValidationSettings = yield select(selectUserValidationPluginSettings);
  const sessionSettings = yield select(selectSessionSettings);

  if (payload.action === 'uploadPlugin') {
    yield call(
      addNewPluginSettings,
      payload.results,
      sessionSettings,
      userValidationSettings,
      orgValidationSettings
    );
  } else if (payload.action === 'deletePlugin') {
    yield call(
      removeDeletedPluginSettings,
      sessionSettings,
      userValidationSettings,
      orgValidationSettings
    );
  }

  yield debounce(3000, UPDATED_SETTINGS, applyShellSettings);
}

export function* watchStatefulPluginSaved() {
  yield takeLatest(STATEFUL_PLUGIN_SAVED, handleStatefulPluginSaved);
}
